<template>
  <div class="day-tabs">
    <el-tabs
      v-loading.fullscreen.lock="isScreenLocked"
      v-model="activeTab"
      :class="{ loading: isScreenLocked }"
      tab-position="left"
      type="card"
    >
      <el-tab-pane v-for="day in filterDays" :key="day.name" :name="day.fixedName">
        <div
          slot="label"
          :class="['day-label', { 'day-label-middle': !canUserEdit }]"
          :accesskey="day.accessKey"
        >
          <p class="day-name-label">{{ day.name }}</p>
          <p class="day-name-label--small">
            {{ day.name.substring(0, 2) }}
          </p>
          <p class="day-date-label">
            {{ day.labelDate }}
          </p>
          <el-checkbox
            v-if="canUserEdit"
            v-model="dayCheckboxes[day.fixedName]"
            class="day-checkbox-label"
            @change="alertDestructive(day.fixedName, day.date)"
          >
            {{ $t('calendar.noMeals') }}
          </el-checkbox>
        </div>

        <div class="meals-info">
          <meals-card
            v-for="mealEvent in mealEvents"
            :key="mealEvent.name"
            :day-name="day.fixedName"
            :meal-event="mealEvent"
            :meals-info="day[mealEvent.name].mealsInfo"
            :menu-event-id="day[mealEvent.name].id"
            :can-user-edit="canUserEdit"
            class="meals-info-card"
          >
            {{ mealEvent.capitalized }}
          </meals-card>
        </div>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>
<script>
import Vue from 'vue';
import { mapState } from 'vuex';
import moment from 'moment';
import { FROM_THIS_MONDAY, DAY_LIST, NAVIGATION_ACCESS_KEYS } from '@/constants';
import MealsCard from '@/components/weeklymenu/MealsCard';
import mapvalues from 'lodash.mapvalues';
import isempty from 'lodash.isempty';
import get from 'lodash.get';

export default {
  name: 'TheCalendar',
  components: {
    MealsCard,
  },
  props: {
    canUserEdit: {
      type: Boolean,
      required: true,
    },
    updateCheckboxes: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dayList: DAY_LIST,
      dayCheckboxes: {
        monday: true,
        tuesday: true,
        wednesday: true,
        thursday: true,
        friday: true,
      },
      activeTab: '',
    };
  },
  computed: {
    ...mapState('orders', { ordersWeek: (state) => state.byUserWeek }),
    ...mapState('user', {
      user: (state) => state.user,
      isRequestingUser: (state) => state.isRequestingUser,
    }),
    ...mapState('weeklyMenu', {
      activeWeeklyMenu: (state) => state.menu,
      weekNumber: (state) => state.weekNumber,
      week: (state) => state.week,
      isRequestingMenu: (state) => state.isRequestingMenu,
    }),
    ...mapState('events', {
      mealEvents: (state) => {
        const noBreakfast = get(Vue.prototype.$flags, 'noBreakfast', false);
        if (noBreakfast) {
          return state.mealEvents.filter((event) => event.name !== 'breakfast');
        }
        return state.mealEvents;
      },
    }),
    isScreenLocked() {
      return this.isRequestingUser || this.isRequestingMenu;
    },
    filterDays() {
      if (this.mealEvents.length <= 0) {
        return {};
      }

      const loadedWeeklyMenu = this.activeWeeklyMenu;
      if (loadedWeeklyMenu.length === 0) {
        const weekDays = [
          { name: 'monday', dayNumber: 1 },
          { name: 'tuesday', dayNumber: 2 },
          { name: 'wednesday', dayNumber: 3 },
          { name: 'thursday', dayNumber: 4 },
          { name: 'friday', dayNumber: 5 },
        ];

        this.mealEvents.forEach((mealEvent, eventIndex) => {
          loadedWeeklyMenu.push(
            ...weekDays.map((weekDay) => {
              const totalDaysPerEvent = weekDays.length;
              const id = weekDay.dayNumber + eventIndex * totalDaysPerEvent;
              const weekDaysAdded = 7 * this.weekNumber;

              return {
                date: moment()
                  .day(weekDay.dayNumber + weekDaysAdded)
                  .format('YYYY-MM-DD'),
                event: mealEvent.id,
                office: this.user.office,
                id,
                mealsInfo: [],
              };
            })
          );
        });
      }

      /* eslint no-param-reassign: 0 */
      return loadedWeeklyMenu.reduce((accumulator, menuItem) => {
        const dayIndex = moment(menuItem.date).isoWeekday() - 1;
        const event = this.mealEvents.find(({ id }) => menuItem.event === id);
        const eventName = get(event, 'name', '');
        if (!eventName) {
          return accumulator;
        }

        const momentWeekDayNumber = FROM_THIS_MONDAY.toThisMonday + parseInt(dayIndex, 10);

        if (!accumulator[dayIndex]) {
          accumulator[dayIndex] = {};
          accumulator[dayIndex].eventList = [];
        }

        accumulator[dayIndex][eventName] = menuItem;
        accumulator[dayIndex].name = this.$t(`calendar.${this.dayList[dayIndex]}Title`);
        accumulator[dayIndex].fixedName = this.dayList[dayIndex];
        accumulator[dayIndex].accessKey =
          NAVIGATION_ACCESS_KEYS.weekDays[accumulator[dayIndex].fixedName];

        const dayOfWeek = momentWeekDayNumber + FROM_THIS_MONDAY.toNextWeek * this.weekNumber;
        const dayDate = moment().day(dayOfWeek);
        accumulator[dayIndex].labelDate = dayDate.format('MMMM DD');
        accumulator[dayIndex].date = dayDate.format('YYYY-MM-DD');

        return accumulator;
      }, {});
    },

    isFilterDaysLoading() {
      return !Object.keys(this.filterDays).length || this.isRequestingMenu;
    },
  },
  watch: {
    filterDays: 'changeActiveTab',
    ordersWeek: 'handleOrdersUpdate',
    updateCheckboxes: 'updateAndResetCheckboxes',
  },
  methods: {
    alertDestructive(dayName, date) {
      this.$emit('alert-destructive', dayName, this.dayCheckboxes[dayName], date);
    },
    updateAndResetCheckboxes() {
      if (this.updateCheckboxes) {
        this.handleOrdersUpdate();
        this.$emit('reset-update-checkboxes');
      }
    },
    handleOrdersUpdate() {
      if (!isempty(this.filterDays)) {
        let dayCheckboxesClone = Object.assign({}, this.dayCheckboxes);
        dayCheckboxesClone = mapvalues(this.dayCheckboxes, () => true);
        Object.values(this.ordersWeek).forEach((order) => {
          Object.values(this.filterDays).forEach((day) => {
            if (order.menuInfo.date.slice(-2) === day.labelDate.slice(-2)) {
              dayCheckboxesClone[day.fixedName] = false;
            }
          });
        });
        this.dayCheckboxes = dayCheckboxesClone;
      }
    },
    changeActiveTab() {
      const dayExists = Object.keys(this.filterDays).some((day) => {
        const name = this.filterDays[day].fixedName;
        return name === this.dayList[moment().isoWeekday() - 1];
      });
      if (!dayExists || this.canUserEdit) {
        this.activeTab = Object.values(this.filterDays)[0].fixedName;
      } else {
        this.activeTab = this.dayList[moment().isoWeekday() - 1];
      }
      this.handleOrdersUpdate();
    },
  },
};
</script>
<style scoped lang="scss">
@import '../../scss/variables';

/deep/.day-tabs {
  .el-tabs__item {
    line-height: 20px;
  }

  .el-checkbox__label {
    font-size: $label-text-size;
  }

  .el-table--fit {
    height: 100%;
    border-radius: 0 0 10px 10px;
  }

  .el-tab-pane {
    min-height: 508px;
  }

  .el-tabs--card > .el-tabs__header .el-tabs__nav {
    border: 0;
    border-left: $table-border-color;
    border-bottom: $table-border-color;
  }

  @media only screen and (max-width: 550px) {
    .el-tabs--card > .el-tabs__header .el-tabs__nav {
      // one column display of meals
      border-radius: 0;
      border: none;
    }
  }

  .el-tabs--left {
    overflow: visible;

    .el-tabs__content {
      border-bottom: $table-border-color;
      border-right: $table-border-color;
      border-left: $table-border-color;
      border-radius: 0 0 10px 10px;
      box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);
    }

    &.loading {
      height: 508px;

      .el-tabs__content {
        box-shadow: none;
        border: 0;
      }
    }

    .meals-info {
      display: flex;
      height: 100%;
    }

    @media only screen and (max-width: 768px) {
      .meals-info {
        // one column display of meals
        display: block;
      }
    }

    .el-tabs__header.is-left {
      margin-right: 0;
      margin-top: 40px;
      border-bottom: none;
    }

    @media only screen and (max-width: 550px) {
      .el-tabs__header.is-left {
        position: fixed;
        left: 0;
        bottom: 0;
        top: auto;
        z-index: 1;
        height: auto;
      }
    }

    &.el-tabs--card {
      .el-tabs__nav {
        border-radius: 0 0 0 10px;
      }

      .el-tabs__item.is-left {
        height: 88px;
        width: 204px;
        text-align: center;
        border-right: $table-border-color;
        border-top: $table-border-color;
        font-weight: normal;
        background-color: $background-tab-color;
        border-radius: 0 0 0 10px;

        .day-label {
          text-align: left;
          padding: 8px 0;

          &.day-label-middle {
            padding: 20px 0;
          }
        }

        .day-name-label--small {
          display: none;
        }

        .day {
          &-name-label {
            font-size: 16px;
            font-weight: normal;
            color: $primary-text-color-tabs;
            margin-top: 4px;
          }

          &-date-label {
            font-size: 12px;
            line-height: normal;
            color: $secondary-text-color-tabs;
            margin-top: 4px;
            &:hover {
              color: $secondary-text-color-tabs;
            }
          }

          &-checkbox-label {
            font-size: 12px;
            display: -webkit-box;
            font-weight: normal;
            color: $primary-text-color-tabs;
            margin-top: 4px;
            width: 80px;
          }
        }

        &.is-active {
          border-right: $active-tab-border;
          border-top: $table-border-color;

          .day {
            &-name-label {
              font-weight: bold;
              color: $secondary-text-color-tabs;
            }

            &-checkbox-label {
              font-weight: normal;
              color: $secondary-text-color-tabs;

              .el-checkbox__inner {
                border: $checkbox-border-color;
              }
            }
          }
        }
      }

      @media only screen and (max-width: 991px) {
        .el-tabs__item.is-left {
          width: 130px;
        }
      }

      @media only screen and (max-width: 550px) {
        .el-tabs__header {
          box-shadow: 0px 0px 16px 0px rgba(0, 0, 0, 0.25);
          border: none;
        }
        .el-tabs__item.is-left {
          height: 60px;
          width: 20vw;
          display: inline-block;
          padding: 0;
          font-size: 20px;
          border-radius: 0;

          &.is-active {
            background-color: #3f484b;
            color: #fff;
          }

          .day-label {
            padding: 20px 0;
          }

          .day-name-label--small {
            text-align: center;
            display: block;
          }
          .day-name-label,
          .day-checkbox-label,
          .day-date-label {
            display: none;
          }
        }
        .el-tabs__item.is-left:last-child {
          border-right: none;
        }
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/weeklymenu/TheCalendar.vue